import { AccessTime, CheckCircle, OpenInNew } from '@mui/icons-material';
import { Box, Button, CircularProgress, Link, Step, StepLabel, Stepper, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import BottomPanel from '../bottom-panel/BottomPanel';
import { ExerciseWithStandaloneSimulation } from '../constants';
import { getSimulationPath } from '../utils/activeSimulation';

export interface ClassroomJoinPanelProps {
  exerciseId?: string;
  exerciseName?: string; // Undefined if not joined
  joinButton?: ReactNode;
  joining?: boolean; // True when we are waiting for the first SignalR message
  leaveButton?: ReactNode;
  onJoin?: () => void;
  position?: string;
  simulationId?: string;
  simulatorLoading?: boolean;
  simulatorReady?: boolean;
  url?: string;
}

const StepIcon = ({ completed, loading }: { completed?: boolean; loading?: boolean }) =>
  completed ? (
    <CheckCircle color="success" />
  ) : loading ? (
    <CircularProgress size={16} />
  ) : (
    <AccessTime color="disabled" />
  );

const ClassroomJoinPanel = ({
  exerciseId,
  exerciseName,
  joinButton,
  joining,
  // leaveButton,
  position,
  simulationId,
  simulatorLoading,
  simulatorReady,
  url,
}: ClassroomJoinPanelProps) => {
  const activeStep = exerciseName ? (position ? (simulatorReady ? 3 : 2) : 1) : 0;

  return (
    <BottomPanel sx={{ minHeight: { xs: 111, sm: 65 } }}>
      {exerciseName || joining ? (
        <Box display={{ sm: 'flex' }} alignItems="center" justifyContent="space-between" gap={3}>
          <Box flexShrink={0}>
            {exerciseName && (
              <>
                {simulationId && exerciseId !== ExerciseWithStandaloneSimulation ? (
                  <Link
                    component={RouterLink}
                    sx={{ lineHeight: 1 }}
                    to={getSimulationPath(true, exerciseId, simulationId)}
                    variant="overline"
                  >
                    {exerciseName}
                  </Link>
                ) : (
                  <Typography variant="overline" color="primary" sx={{ lineHeight: 1 }}>
                    {exerciseName}
                  </Typography>
                )}
              </>
            )}
            <Typography variant="body1">You can start the simulation when the simulator is ready.</Typography>
          </Box>
          <Stepper sx={{ width: '100%' }} activeStep={activeStep}>
            <Step>
              <StepLabel StepIconComponent={iconProps => <StepIcon {...iconProps} loading={joining} />}>
                {exerciseName ? 'Joined session' : 'Joining session'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel
                optional={position && <Typography variant="caption">{position}</Typography>}
                StepIconComponent={StepIcon}
              >
                {position ? 'Position assigned' : 'Position not assigned'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel StepIconComponent={iconProps => <StepIcon {...iconProps} loading={simulatorLoading} />}>
                {simulatorReady ? 'Simulator ready' : simulatorLoading ? 'Simulation loading' : 'Simulator not ready'}
              </StepLabel>
            </Step>
          </Stepper>

          <Box display="flex" gap={1}>
            {/* 
                // TODO
                Hide Leave button until implementing a fix for https://dev.azure.com/maritimesim/Nemo/_workitems/edit/357608 
                Leave functionality is not working.
            */}
            {/* {leaveButton} */}
            <Button
              component="a"
              disabled={!url}
              href={url}
              rel="noreferrer"
              startIcon={<OpenInNew />}
              sx={{ flexShrink: 0 }}
              target="_blank"
              variant="contained"
            >
              Start
            </Button>
          </Box>
        </Box>
      ) : (
        <Box display={{ sm: 'flex' }} alignItems="center" justifyContent="space-between" gap={3}>
          <Typography variant="body2" sx={{ lineHeight: 1 }}>
            An instructor has invited you for a classroom session.
          </Typography>

          <Box mt={{ xs: 4, sm: 0 }}>{joinButton}</Box>
        </Box>
      )}
    </BottomPanel>
  );
};

export default ClassroomJoinPanel;
